import React from 'react';
import { Link } from 'react-router-dom';

/** Format a count with a space as the thousands separator (e.g. 12456 → "12 456"). */
export const formatCount = (value: number): string =>
  value.toLocaleString('en-US').replace(/,/g, ' ');

export type StatCardProps = {
  /** Heading shown at the top-left of the card. */
  title: string;
  /**
   * The value to display. A number is formatted with a thousands separator; a
   * string (e.g. the "13/12" signups ratio) is rendered as-is. `null` while
   * loading.
   */
  value: number | string | null;
  /** Route the "View All" button (and card) links to — the module's index. */
  to: string;
  /** Accent colour (hex) used for the "View All" button. */
  accentColor: string;
  /** Whether the stats are still loading. */
  isPending?: boolean;
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  to,
  accentColor,
  isPending = false,
}) => {
  const showValue = !isPending && value !== null;
  const display = typeof value === 'number' ? formatCount(value) : value;

  return (
    <div
      className="card card-stretch mb-5 mb-xxl-8 bg-white"
      data-testid="stat-card"
    >
      <div className="card-body d-flex flex-column justify-content-between">
        {/* begin::Header */}
        <span className="text-gray-800 fw-bold fs-5">{title}</span>
        {/* end::Header */}

        {/* begin::Value */}
        <div
          className="text-dark fw-bolder fs-1 my-5"
          data-testid="stat-value"
        >
          {showValue ? display : '—'}
        </div>
        {/* end::Value */}

        {/* begin::Actions */}
        <div className="d-flex align-items-center">
          <Link
            to={to}
            className="btn btn-sm text-white fw-bold"
            style={{ backgroundColor: accentColor }}
          >
            View All
          </Link>
        </div>
        {/* end::Actions */}
      </div>
    </div>
  );
};
